import { Typography } from '@mui/material';
import { ObjectDetailsContentLayout } from 'framework/ViewTypes/contentLayout';
import { ObjectDetailsSections } from 'framework/ViewTypes/embeddable';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';
import { POSITION_FILL_REQUEST_BADLE_STATUS_COLOR, POSITION_PROFILE_STATUS_LABELS } from '../../constants';
import { getPositionProfileHeaderActions } from '../header/headerActions';
import type { PositionProfileDetailsViewModel } from '../model/positionProfileDetailsMapper';
import PositionProfileProcessTabs from '../tabs/PositionProfileProcessTabs';
import SalaryRanges from './SalaryRanges';

type PositionProfileDetailsContentProps = {
  profile?: PositionProfileDetailsViewModel;
  loading?: boolean;
  error?: Error | null;
  retry?: () => void | Promise<unknown>;
  onEdit: () => void;
  onDeprecate: () => void;
  onReactivate: () => void;
  onRetire: () => void;
  permissions: {
    canUpdate: boolean;
    canDeprecate: boolean;
    canReactivate: boolean;
    canRetire: boolean;
    canCreateTimelineEntry: boolean;
    canReadTimeline: boolean;
    canReadPositions: boolean;
  };
};

const PositionProfileDetailsContent = ({
  profile,
  loading,
  error,
  retry,
  onEdit,
  onDeprecate,
  onReactivate,
  onRetire,
  permissions,
}: PositionProfileDetailsContentProps) => {
  return (
    <ObjectDetailsContentLayout
      data={profile}
      loading={loading}
      error={error}
      retry={retry}
      notFoundMessage="Position profile not found."
      renderContent={loadedProfile => ({
        header: {
          title: loadedProfile.name,
          badges: [
            {
              id: 'status',
              label: POSITION_PROFILE_STATUS_LABELS[loadedProfile.status] || loadedProfile.status,
              color: POSITION_FILL_REQUEST_BADLE_STATUS_COLOR[loadedProfile.status] || 'default',
            },
          ],
          attributes: [
            { id: 'specializationId', value: loadedProfile.specializationLabel },
            { id: 'technologyId', value: loadedProfile.technologyLabel },
            { id: 'competencyLevel', value: loadedProfile.competencyLevel },
          ],
          actions: getPositionProfileHeaderActions(
            loadedProfile,
            {
              onEdit,
              onDeprecate,
              onReactivate,
              onRetire,
            },
            permissions,
          ),
        },
        leftSide: (
          <ObjectDetailsSections
            sections={[
              {
                title: 'Description',
                content: <Typography variant="body1">{loadedProfile.description || EMPTY_VALUE_INDICATOR}</Typography>,
              },
              { title: 'Salary ranges', content: <SalaryRanges ranges={loadedProfile.salaryRanges} /> },
            ]}
          />
        ),
        rightSide: (
          <PositionProfileProcessTabs
            profileId={loadedProfile.id}
            canReadTimeline={permissions.canReadTimeline}
            canManageNotes={permissions.canCreateTimelineEntry}
            canReadPositions={permissions.canReadPositions}
          />
        ),
      })}
    />
  );
};

export default PositionProfileDetailsContent;
